import * as React from "react";
import { z } from "zod";
import { DynamicForm } from "../../util/DynamicForm";
import { useGeneratedForm } from "../../hooks/useGeneratedForm";
import { apiPost, apiGet } from "../../util/axiosUtil";
import API_URL from "../../config/apiConfig";
import MutationHandler from "../../util/MutationHandler";

const CREATE_HOST_URL = `${API_URL}/api/subscribers`;
const FETCH_PLANS_URL = `${API_URL}/api/plans`;

export default function CreateHostForm({ onCancel, onSuccess }) {
  const [planOptions, setPlanOptions] = React.useState([]);
  const [loadingPlans, setLoadingPlans] = React.useState(true);

  // Fetch available plans
  React.useEffect(() => {
    const fetchPlans = async () => {
      // await new Promise((resolve) => setTimeout(resolve, 3000));

      try {
        const response = await apiGet(FETCH_PLANS_URL);
        const options =
          response?.data?.data?.map((plan) => ({
            id: plan.id,
            name: plan.name,
          })) || [];
        setPlanOptions(options);
        setLoadingPlans(false);
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };

    fetchPlans();
  }, []);

  const formConfig = {
    name: {
      label: "Name",
      type: "text",
      placeholder: "Enter your name",
      defaultValue: "",
      required: true,
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Name is required",
          });
        } else if (value.length < 3) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Name should be more than 3 letters",
          });
        }
      }),
    },
    username: {
      label: "Username",
      type: "text",
      placeholder: "Enter username",
      defaultValue: "",
      required: true,
      normalize: (val) => val.toLowerCase().replace(/\s+/g, ""),
      validation: z
        .string()
        .transform((val) => val.toLowerCase().replace(/\s+/g, "")) // ✅ auto-make lowercase and remove spaces
        .superRefine((value, ctx) => {
          if (!value.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Username is required",
            });
          } else if (value.length < 3) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Username should be more than 3 letters",
            });
          } else if (!/^[a-z0-9_]+$/.test(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                "Username can only contain lowercase letters, numbers, and underscores",
            });
          }
        }),
    },
    password: {
      label: "Password",
      type: "password",
      placeholder: "Enter password",
      defaultValue: "",
      required: true,
      normalize: (val) => val.replace(/\s+/g, ""),
      validation: z
        .string()
        .nonempty("Password is required") // cannot be empty
        .min(4, "Password must be at least 4 characters") // min 4 chars
        .refine((val) => !/\s/.test(val), {
          message: "Password cannot contain spaces", // reject spaces
        }),
    },
    plan: {
      label: "Subscription Plan",
      type: "select",
      options: planOptions,
      defaultValue: "",
      required: true,
      isLoadingOptions: loadingPlans, // ✅ pass loading state here
      validation: z.preprocess(
        (val) => (val === "" ? 0 : Number(val)),
        z.number().superRefine((value, ctx) => {
          if (value < 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Subscription is required",
            });
          }
        }),
      ),
    },
  };

  return (
    <MutationHandler
      mutationFn={(formData) => apiPost(CREATE_HOST_URL, formData)}
      onCancel={onCancel}
      onSuccess={onSuccess}
    >
      {(mutation) => (
        <HostForm
          formConfig={formConfig}
          onSubmit={(value) => mutation.mutate(value)}
          isLoading={mutation.isPending}
          onCancel={onCancel}
        />
      )}
    </MutationHandler>
  );
}

function HostForm({ formConfig, onSubmit, isLoading, onCancel }) {
  const form = useGeneratedForm(formConfig, async ({ value }) => {
    onSubmit(value);
  });

  return (
    <DynamicForm
      form={form}
      formConfig={formConfig}
      isLoading={isLoading}
      onCancel={onCancel}
      buttonText={"Create Subscriber"}
    />
  );
}
